// Importação do Firebase
import axios from 'axios';
import { initializeApp } from 'firebase/app';
import { 
  initializeFirestore, 
  persistentLocalCache, 
  persistentMultipleTabManager 
} from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth'; // Autenticação
import { getStorage } from 'firebase/storage'; // Storage

// ─────────────────────────────────────────────────────────────────────────
// VALIDAÇÃO DE VARIÁVEIS DE AMBIENTE
// ─────────────────────────────────────────────────────────────────────────
function validateEnvironmentVariables() {
  const requiredVars = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_DATABASE_URL',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID',
  ];

  const missingVars = requiredVars.filter(
    varName => !process.env[varName] || process.env[varName] === 'undefined'
  );

  if (missingVars.length > 0) {
    console.error(
      '❌ ERRO CRÍTICO: Variáveis de ambiente do Firebase não configuradas!\n\n' +
      'Variáveis faltando:\n' +
      missingVars.map(v => `  • ${v}`).join('\n') +
      '\n\n⚠️  SOLUÇÃO:\n' +
      '  1. Copie o arquivo .env.example para .env:\n' +
      '     cp .env.example .env\n' +
      '  2. Preencha as variáveis com suas credenciais do Firebase\n' +
      '  3. Em PRODUÇÃO: Configure as variáveis no painel de deploy (Vercel, Heroku, etc.)\n' +
      '  4. Para mais informações, veja README.md\n'
    );
    
    // Em produção, lance um erro. Em dev, apenas aviso.
    if (process.env.NODE_ENV === 'production') {
      throw new Error('Variáveis de ambiente do Firebase não configuradas');
    }
  }
}

// Executa a validação
validateEnvironmentVariables();

// Configuração do Firebase com suas credenciais
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

// Inicializa o Firestore com CACHE OFFLINE (Economiza leituras ao banco de dados)
const db = initializeFirestore(app, {
  localCache: persistentLocalCache({tabManager: persistentMultipleTabManager()})
});

// Inicializa a autenticação padrão (Lojista)
const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error("Erro ao configurar persistência do Firebase Auth:", error);
});

// Inicializa o Storage
const storage = getStorage(app);

// ─────────────────────────────────────────────────────────────────────────
// Interceptor global do Axios: anexa o Firebase ID Token (Authorization:
// Bearer) automaticamente nas chamadas ao backend Auuii. Só anexa quando a
// URL aponta para o backend (evita vazar o token para APIs de terceiros).
// ─────────────────────────────────────────────────────────────────────────
function isBackendUrl(url = '') {
  if (!url) return false;
  if (url.startsWith('/')) return true;                 // relativa → mesma origem do backend
  // Lida na hora (não no import): a URL vem do settings/appConfig e pode chegar
  // depois deste módulo carregar. require tardio evita ciclo (appConfig importa db daqui).
  const { getApiUrl } = require('./appConfig');
  const backend = getApiUrl();
  if (backend && url.startsWith(backend)) return true;
  return false;
}

axios.interceptors.request.use(async (config) => {
  try {
    const url = config.url || '';
    if (isBackendUrl(url) && auth.currentUser) {
      const token = await auth.currentUser.getIdToken();
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (err) {
    console.warn('[apiAuth] Falha ao anexar ID token:', err?.message);
  }
  return config;
});

/**
 * Helper para chamadas via fetch ao backend já com o ID token.
 * Use no lugar de fetch() quando o destino for o backend Auuii.
 */
export async function authorizedFetch(input, init = {}) {
  const headers = new Headers(init.headers || {});
  try {
    if (auth.currentUser) {
      const token = await auth.currentUser.getIdToken();
      headers.set('Authorization', `Bearer ${token}`);
    }
  } catch (err) {
    console.warn('[apiAuth] Falha ao anexar ID token (fetch):', err?.message);
  }
  return fetch(input, { ...init, headers });
}

export { db, auth, storage };
